import { FormattedMessage, useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { CrudForm } from '~/eds/src/modules/CrudForm/CrudForm';
import { DataFieldType } from '~/enums/DataFieldType';
import { FieldTypeSelect } from '~/features/fields/FieldTypeSelect/FieldTypeSelect';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { TableColumnSpecState, TableSpecState } from '../../../types/table';
import { CHARACTER_LIMIT } from '../constants';

interface Props {
  isNew: boolean;
  readOnly: boolean;
  tableSpec: TableSpecState;
  onChange: (updatedTableSpec: TableSpecState) => void;
}

export const TableSpecConfiguration = ({
  isNew,
  readOnly,
  tableSpec,
  onChange,
}: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();

  const { columns, key, name } = tableSpec;

  const instructionsInputs = columns.map(
    ({ name: columnName, description: columnDescription = '' }, index) => {
      if (!columnName) return null;
      return (
        <FormField<string, false>
          footer={intl.formatMessage(
            {
              id: 'TableSpecConfiguration.tableColumnInstructionsFooter',
              defaultMessage: '{count} / {limit} characters',
              description:
                'Counter indicating if input text is within the limit. Example: count = 820, limit = 1000',
            },
            {
              count: formatNumber(columnDescription.length),
              limit: formatNumber(CHARACTER_LIMIT),
            },
          )}
          error={
            columnDescription.length > CHARACTER_LIMIT
              ? intl.formatMessage(
                  {
                    id: 'TableSpecConfiguration.tableColumnInstructionsError',
                    defaultMessage:
                      '{count} / {limit} characters. Shorten {columnName} instructions.',
                    description:
                      'Counter indicating when input text is over the limit. Example: count = 1050, limit = 1000. columnName = Invoice Date',
                  },
                  {
                    count: formatNumber(columnDescription.length),
                    limit: formatNumber(CHARACTER_LIMIT),
                    columnName,
                  },
                )
              : undefined
          }
          input={TextArea}
          inputProps={{
            rows: 1,
          }}
          key={`'instructions-'${index}`}
          label={intl.formatMessage(
            {
              id: 'TableSpecConfiguration.tableColumnInstructionsLabel',
              defaultMessage: '{column} Instructions',
              description:
                'Label for the column instructions. Example: column = "Email Address"',
            },
            { column: columnName },
          )}
          name="table-spec-configuration-column-instructions-input"
          placeholder={intl.formatMessage(
            {
              id: 'TableSpecConfiguration.tableColumnInstructionsPlaceholder',
              defaultMessage: 'Instructions for the {column} attribute',
              description:
                'Placeholder for the column instructions. Example: column = "Email Address"',
            },
            { column: columnName },
          )}
          value={columnDescription}
          onChange={(updatedColumnInstructions) =>
            onChange({
              ...tableSpec,
              columns: columns.map((column) =>
                column.name === columnName
                  ? { ...column, description: updatedColumnInstructions ?? '' }
                  : column,
              ),
            })
          }
        />
      );
    },
  );

  return (
    <Layout preset="form-fields">
      <FormField<string, false>
        input={TextInput}
        label={intl.formatMessage({
          id: 'TableSpecConfiguration.tableNameLabel',
          defaultMessage: 'Table Name',
          description: 'Label for the name of the table being configured',
        })}
        name="table-spec-name-configuration"
        placeholder={intl.formatMessage({
          id: 'TableSpecConfiguration.tableNamePlaceholder',
          defaultMessage: 'Name the table',
          description: 'Placeholder text for the table name input',
        })}
        readOnly={readOnly}
        value={name}
        onChange={(updatedName) =>
          onChange({ ...tableSpec, name: updatedName ?? '' })
        }
      />
      <FormField<string, false>
        info={intl.formatMessage({
          id: 'TableSpecConfiguration.tableKeyInfo',
          defaultMessage: 'Must be unique across existing table models.',
          description: 'Info text for the table key',
        })}
        input={TextInput}
        label={intl.formatMessage({
          id: 'TableSpecConfiguration.tableKeyLabel',
          defaultMessage: 'Table Key',
          description: 'Label for the key of the table being configured',
        })}
        name="table-spec-key-configuration"
        placeholder={`${name.toLowerCase().split(' ').join('-')}`}
        readOnly={!isNew || readOnly}
        value={key}
        onChange={(updatedKey) =>
          onChange({ ...tableSpec, key: updatedKey ?? '' })
        }
      />
      <Layout direction="column" spacing={1}>
        <Label>
          <FormattedMessage
            id="TableSpecConfiguration.tableColumnsConfiguration"
            defaultMessage="Table Attributes (Columns)"
            description="Title for the table columns configuration form"
          />
        </Label>
        <CrudForm<any, false, TableColumnSpecState>
          actions={{
            add: {
              text: intl.formatMessage({
                id: 'TableSpecConfiguration.addTableColumnText',
                defaultMessage: 'Add Table Column',
                description: 'Text for add table column button',
              }),
            },
            delete: (_datum) => ({
              tooltip: intl.formatMessage({
                id: 'TableSpecConfiguration.removeTableColumnTooltip',
                defaultMessage: 'Remove table column',
                description: 'Tooltip for remove table column action',
              }),
            }),
          }}
          columns={[
            {
              key: 'name',
              input: TextInput,
              inputProps: {
                width: 'auto',
              },
              label: intl.formatMessage({
                id: 'TableSpecConfiguration.tableColumnLabel',
                defaultMessage: 'Attribute Name',
                description: 'Label for table column name',
              }),
              name: 'table-spec-column-configuration-input',
              placeholder: intl.formatMessage({
                id: 'TableSpecConfiguration.tableColumnPlaceholder',
                defaultMessage: 'Name the column',
                description: 'Placeholder for table column name',
              }),
              value: (datum: TableColumnSpecState) => datum.name,
              onChange: () => {},
            },
            {
              key: 'columnType',
              input: FieldTypeSelect,
              inputProps: {
                config: {
                  acceptFieldTypes: [
                    DataFieldType.DATE,
                    DataFieldType.NUMBER,
                    DataFieldType.STRING,
                    DataFieldType.TEXT_AREA,
                  ],
                },
                width: 'auto',
              },
              label: intl.formatMessage({
                id: 'TableSpecConfiguration.tableColumnFieldTypeLabel',
                defaultMessage: 'Data Type',
                description: 'Label for data type select',
              }),
              name: 'table-spec-data-type-configuration-select',
              placeholder: intl.formatMessage({
                id: 'TableSpecConfiguration.tableColumnDataTypePlaceholder',
                defaultMessage: 'Select data type',
                description: 'Placeholder for data type select',
              }),
              value: (datum: TableColumnSpecState) => datum.columnType,
              onChange: () => {},
            },
          ]}
          columnWidths={['1fr', '1fr']}
          data={columns}
          disabled={readOnly}
          onUpdate={(updatedColumns) =>
            onChange({
              ...tableSpec,
              columns: updatedColumns,
            })
          }
        />
      </Layout>
      {instructionsInputs}
    </Layout>
  );
};
